import '@xyflow/react/dist/style.css'

import {
  ConnectionMode,
  DefaultEdgeOptions,
  MarkerType,
  Panel,
  ReactFlow,
} from '@xyflow/react'
import { CantileverButton } from 'components/CantileverButton'
import { PageHeader } from 'components/PageHeader'
import { TooltipWrapper } from 'components/TooltipWrapper'
import {
  ClipboardPaste,
  Copy,
  Redo2,
  RotateCcw,
  RotateCw,
  Undo2,
} from 'lucide-react'
import { useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { PfdEdge, PfdNode } from 'types'
import { v4 as uuidv4 } from 'uuid'

import DashedLine from './components/edges/DashedLine'
import SolidLine from './components/edges/SolidLine'
import { CategorizedNodeLibrary } from './components/library/CategorizedNodeLibrary'
import NodePropertyPanel from './components/nodes/propertyPanel/NodePropertyPanel'
import { ProcessFlowDiagramBuilderContext } from './contexts/ProcessFlowDiagramBuilderContext'
import { createNodeTypesMapping } from './data/MinimalSymbolLibrary'
import { useDragLibrary } from './hooks/useDragLibrary'
import { CommonStyles } from './svg/CommonStyles'
import { EdgeType } from './types'
import { insertGhostNodeInEdge } from './utils/helpers'

const UndoIcon = styled(Undo2)``

const RedoIcon = styled(Redo2)``

const CopyIcon = styled(Copy)``

const PasteIcon = styled(ClipboardPaste)``

const RotateCCWIcon = styled(RotateCcw)``

const RotateCWIcon = styled(RotateCw)``

export const ImageContainer = styled.div<{
  $clickable?: boolean
  $disabled?: boolean
  $clipboard?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  border-radius: 0.25rem;
  transition: all 0.2s;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};

  ${({ $clickable, $disabled, theme }) =>
    $clickable &&
    !$disabled &&
    `
    &:hover {
      background-color: ${theme.forecast.background4};
    }
  `}
`

const Container = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) =>
    theme.forecast.pageBackground1 || theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.activeText};
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
`

const ButtonContainer = styled.div`
  display: flex;
  padding-right: 1.5rem;
  gap: 1rem;
`

const ReactFlowContainer = styled.div`
  flex: 1;
  height: 100%;
  position: relative;
`

const PopupOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`

const PopupContainer = styled.div<{ x: number; y: number }>`
  position: fixed;
  left: ${({ x }) => x}px;
  top: ${({ y }) => y}px;
  z-index: 1000;
  transform: translate(-50%, -100%) translateY(-12px);
  pointer-events: auto;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem;
  background-color: ${({ theme }) => theme.forecast.background2};
`

const MenuBarContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 3.5rem;
  background-color: ${({ theme }) => theme.forecast.background1};
  padding: 0 2rem;
`

const MenuBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
`

const MainContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
`

const TitleInputWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background-color: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.activeText};
  border-radius: 0.5rem;
  padding: 0.125rem 0.5rem;
`

const TitleTextArea = styled.textarea`
  resize: none;
  width: 100%;
  cursor: text;
  color: ${({ theme }) => theme.forecast.activeText};
  background-color: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  padding: 0;
`

const GhostNodeTooltip = styled.div`
  background: rgba(0, 123, 255, 0.1);
  border: ${({ theme }) =>
    `0.0625rem solid ${theme.common.badge.background.blue}`};
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-size: 0.75rem;
  color: white;
`

interface ProcessFlowDiagramBuilderProps {
  viewOnly: boolean
  reactFlowClassName?: string
  defaultEdgeOptionsOverride?: Partial<DefaultEdgeOptions>
  labelVisibilityOnHover?: boolean
  hideHandleConnectionIndicators?: boolean
  onNodeHover?: (node: PfdNode | null, event: React.MouseEvent) => void
  hideHeader?: boolean
  hideMenuBar?: boolean
}

export const ProcessFlowDiagramBuilder = ({
  viewOnly,
  reactFlowClassName,
  defaultEdgeOptionsOverride,
  onNodeHover,
  hideHeader = false,
  hideMenuBar = false,
}: ProcessFlowDiagramBuilderProps) => {
  const {
    pfd,
    setPfd,
    clipboard,
    setClipboard,
    undoActionStack,
    setUndoActionStack,
    redoActionStack,
    setRedoActionStack,
    addToUndoStackOnNewAction,
    handleSaveToRolodex,
    onNodeDragStop,
    onNodeDragStart,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onConnectEnd,
    metadata,
    setMetadata,
    inEditMode,
    setInEditMode,
    editingEnabled,
    onNodeClick,
    renderNodePopup,
    selectedNode,
    setSelectedNode,
    selectedEdge,
    setSelectedEdge,
  } = useContext(ProcessFlowDiagramBuilderContext)

  const inViewMode = viewOnly || !editingEnabled
  const { onDragOver, onDrop, onInit, reactFlowWrapper } = useDragLibrary()
  const [popupPosition, setPopupPosition] = useState<{
    x: number
    y: number
  } | null>(null)

  const handleRotation = useCallback(
    (degrees: number) => {
      if (!selectedNode || inViewMode) return

      const currentRotation = selectedNode.data.rotation ?? 0
      const newRotation = (currentRotation + degrees) % 360

      addToUndoStackOnNewAction(pfd)

      const updatedNode = {
        ...selectedNode,
        data: { ...selectedNode.data, rotation: newRotation },
      }

      setPfd((currentPfd) => ({
        ...currentPfd,
        nodes: currentPfd.nodes.map((node) =>
          node.id === selectedNode.id ? updatedNode : node
        ),
      }))

      setSelectedNode(updatedNode)
    },
    [
      addToUndoStackOnNewAction,
      pfd,
      selectedNode,
      inViewMode,
      setPfd,
      setSelectedNode,
    ]
  )

  const handleCopy = useCallback(() => {
    if (!selectedNode || inViewMode) return

    setClipboard(selectedNode)
  }, [setClipboard, selectedNode, inViewMode])

  const handlePaste = useCallback(() => {
    if (!clipboard || inViewMode) return

    const newNode: PfdNode = {
      ...clipboard,
      id: `${clipboard.type}-${uuidv4().slice(0, 8)}`,
      position: {
        x: clipboard.position.x + 50,
        y: clipboard.position.y + 50,
      },
      data: {
        ...clipboard.data,
        label: `New ${clipboard.type}`,
      },
    }

    addToUndoStackOnNewAction(pfd)

    setPfd((currentPfd) => ({
      ...currentPfd,
      nodes: [...currentPfd.nodes, newNode],
    }))

    setSelectedNode(newNode)
  }, [
    clipboard,
    inViewMode,
    setPfd,
    addToUndoStackOnNewAction,
    pfd,
    setSelectedNode,
  ])

  const handleUndo = useCallback(() => {
    if (inViewMode && undoActionStack.length === 0) return

    setRedoActionStack((prev) => {
      const newStack = [...prev, pfd]
      return newStack.length > 20 ? newStack.slice(1) : newStack
    })

    const undoPfd = undoActionStack[undoActionStack.length - 1]

    setUndoActionStack((prev) => prev.slice(0, -1))

    setPfd(undoPfd)
  }, [
    inViewMode,
    undoActionStack,
    pfd,
    setPfd,
    setRedoActionStack,
    setUndoActionStack,
  ])

  const handleRedo = useCallback(() => {
    if (inViewMode && redoActionStack.length === 0) return

    setUndoActionStack((prev) => {
      const newStack = [...prev, pfd]
      return newStack.length > 20 ? newStack.slice(1) : newStack
    })

    const redoPfd = redoActionStack[redoActionStack.length - 1]
    setRedoActionStack((prev) => prev.slice(0, -1))
    setPfd(redoPfd)
  }, [
    inViewMode,
    redoActionStack,
    pfd,
    setPfd,
    setRedoActionStack,
    setUndoActionStack,
  ])

  const handleLibraryDragStart = () => {}

  const nodeTypes = createNodeTypesMapping()
  const edgeTypes = {
    [EdgeType.SOLID_LINE]: SolidLine,
    [EdgeType.DASHED_LINE]: DashedLine,
  }

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: PfdNode) => {
      if (onNodeClick) {
        onNodeClick(node)
      }
      setSelectedNode(node)

      if (renderNodePopup) {
        setPopupPosition({
          x: event.clientX,
          y: event.clientY,
        })
      }
    },
    [onNodeClick, renderNodePopup, setSelectedNode]
  )

  const handleClosePopup = useCallback(() => {
    setSelectedNode(null)
    setPopupPosition(null)
  }, [setSelectedNode])

  const handlePaneClick = useCallback(() => {
    if (selectedNode) {
      handleClosePopup()
    }
    if (selectedEdge) {
      setSelectedEdge(null)
    }
  }, [selectedNode, selectedEdge, handleClosePopup, setSelectedEdge])

  const handleNodeMouseEnter = useCallback(
    (event: React.MouseEvent, node: PfdNode) => {
      if (onNodeHover) onNodeHover(node, event)
    },
    [onNodeHover]
  )

  const handleNodeMouseLeave = useCallback(
    // react-flow's onNodeMouseLeave has two arguments: event and _node
    // we only want to pass the event to the onNodeHover callback
    (event: React.MouseEvent, _node: PfdNode) => {
      void _node
      if (onNodeHover) onNodeHover(null, event)
    },
    [onNodeHover]
  )

  const handleEdgeClick = useCallback(
    (event: React.MouseEvent, edge: PfdEdge) => {
      if (inViewMode) return
      setSelectedEdge(edge)
    },
    [setSelectedEdge, inViewMode]
  )

  const handleInsertGhostNodeInEdge = useCallback(() => {
    if (!selectedEdge || inViewMode) return

    const result = insertGhostNodeInEdge(selectedEdge, pfd)
    if (!result) return

    setPfd(result.updatedPfd)
    setSelectedEdge(null)
  }, [selectedEdge, pfd, inViewMode, setPfd, setSelectedEdge])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'c' && selectedEdge && !viewOnly && !editingEnabled) {
        event.preventDefault()
        handleInsertGhostNodeInEdge()
      }

      if (event.ctrlKey && !inViewMode) {
        switch (event.key.toLowerCase()) {
          case 'c':
            if (selectedNode) {
              event.preventDefault()
              handleCopy()
            }
            break
          case 'v':
            if (clipboard) {
              event.preventDefault()
              handlePaste()
            }
            break
          case 'r':
            if (selectedNode && !event.shiftKey) {
              event.preventDefault()
              handleRotation(90)
            }
            // counter clockwise, Ctrl + Shift + R
            if (selectedNode && event.shiftKey) {
              event.preventDefault()
              handleRotation(270)
            }
            break
          case 'z':
            if (undoActionStack.length > 0) {
              event.preventDefault()
              handleUndo()
            }
            break
          case 'y':
            if (redoActionStack.length > 0) {
              event.preventDefault()
              handleRedo()
            }
            break
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [
    selectedEdge,
    viewOnly,
    editingEnabled,
    handleInsertGhostNodeInEdge,
    clipboard,
    handleCopy,
    handlePaste,
    handleRedo,
    handleRotation,
    handleUndo,
    inViewMode,
    redoActionStack.length,
    selectedNode,
    undoActionStack.length,
  ])

  const menuBarIcons = [
    {
      label: 'Undo last action (Ctrl + Z)',
      icon: <UndoIcon />,
      onClick: () => handleUndo(),
      requiresUndoAction: true,
    },
    {
      label: 'Redo last action (Ctrl + Y)',
      icon: <RedoIcon />,
      onClick: () => handleRedo(),
      requiresRedoAction: true,
    },
    {
      label: 'Copy node (Ctrl + C)',
      icon: <CopyIcon />,
      onClick: selectedNode ? () => handleCopy() : undefined,
      requiresSelection: true,
    },
    {
      label: 'Paste node (Ctrl + V)',
      icon: <PasteIcon />,
      onClick: () => handlePaste(),
      requiresClipboard: true,
    },
    {
      label: 'Rotate node counter-clockwise (Ctrl + Shift + R)',
      icon: <RotateCCWIcon />,
      onClick: selectedNode ? () => handleRotation(270) : undefined,
      requiresSelection: true,
    },
    {
      label: 'Rotate node clockwise (Ctrl + R)',
      icon: <RotateCWIcon />,
      onClick: selectedNode ? () => handleRotation(90) : undefined,
      requiresSelection: true,
    },
  ]

  return (
    <>
      {!hideHeader && (
        <HeaderContainer>
          <PageHeader
            titleContent={
              inViewMode ? (
                <span style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  {metadata.name}
                </span>
              ) : (
                <TitleInputWrapper>
                  <TitleTextArea
                    value={metadata.name}
                    onChange={(e) =>
                      setMetadata({ name: e.currentTarget.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !(e.shiftKey || e.altKey)) {
                        e.preventDefault()
                        ;(e.currentTarget as HTMLTextAreaElement).blur()
                      }
                    }}
                    rows={1}
                  />
                </TitleInputWrapper>
              )
            }
            trailingContent={
              !viewOnly && (
                <ButtonContainer>
                  <CantileverButton onClick={() => setInEditMode(!inEditMode)}>
                    {inEditMode ? 'Disable Editing' : 'Enable Editing'}
                  </CantileverButton>
                  <CantileverButton onClick={handleSaveToRolodex} primary>
                    Save
                  </CantileverButton>
                </ButtonContainer>
              )
            }
          />
        </HeaderContainer>
      )}
      <Container>
        {!viewOnly && (
          <CategorizedNodeLibrary onDragStart={handleLibraryDragStart} />
        )}
        <MainContentArea>
          {!hideMenuBar && (
            <MenuBarContainer>
              <MenuBar>
                {menuBarIcons.map((navItem, index) => {
                  const isDisabled =
                    inViewMode ||
                    (navItem.requiresSelection && !selectedNode) ||
                    (navItem.requiresClipboard && !clipboard) ||
                    (navItem.requiresUndoAction &&
                      undoActionStack.length === 0) ||
                    (navItem.requiresRedoAction && redoActionStack.length === 0)
                  const isClickable =
                    !inViewMode && !!navItem.onClick && !isDisabled
                  return (
                    <TooltipWrapper
                      key={index}
                      side='bottom'
                      label={navItem.label}>
                      <ImageContainer
                        $clickable={isClickable}
                        $disabled={isDisabled}
                        onClick={isClickable ? navItem.onClick : undefined}>
                        {navItem.icon}
                      </ImageContainer>
                    </TooltipWrapper>
                  )
                })}
              </MenuBar>
            </MenuBarContainer>
          )}
          <ReactFlowContainer ref={reactFlowWrapper}>
            <ReactFlow
              nodes={pfd.nodes}
              edges={pfd.edges}
              connectionMode={ConnectionMode.Loose}
              onNodeDragStop={inViewMode ? undefined : onNodeDragStop}
              onNodeDragStart={inViewMode ? undefined : onNodeDragStart}
              onNodesChange={inViewMode ? undefined : onNodesChange}
              onEdgesChange={inViewMode ? undefined : onEdgesChange}
              onConnect={inViewMode ? undefined : onConnect}
              onConnectEnd={inViewMode ? undefined : onConnectEnd}
              onDragOver={inViewMode ? undefined : onDragOver}
              onDrop={inViewMode ? undefined : onDrop}
              onInit={onInit}
              onNodeClick={handleNodeClick}
              onEdgeClick={handleEdgeClick}
              onPaneClick={handlePaneClick}
              onNodeMouseEnter={handleNodeMouseEnter}
              onNodeMouseLeave={handleNodeMouseLeave}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultEdgeOptions={
                (defaultEdgeOptionsOverride as DefaultEdgeOptions) ?? {
                  type: 'solidLine',
                  markerEnd: { type: MarkerType.Arrow },
                }
              }
              nodesDraggable={!viewOnly && editingEnabled}
              nodesConnectable={!viewOnly && editingEnabled}
              elementsSelectable={!viewOnly && editingEnabled}
              fitView
              className={reactFlowClassName}>
              <CommonStyles />
              <Panel position='top-right'>
                {!viewOnly && selectedNode && (
                  <NodePropertyPanel selectedNode={selectedNode} />
                )}
              </Panel>
              {selectedEdge && !viewOnly && !editingEnabled && (
                <Panel position='top-left'>
                  <GhostNodeTooltip>
                    Press &apos;C&apos; to insert breakpoint on selected edge
                  </GhostNodeTooltip>
                </Panel>
              )}
            </ReactFlow>
          </ReactFlowContainer>
        </MainContentArea>
        {selectedNode && renderNodePopup && popupPosition && (
          <PopupOverlay>
            <PopupContainer x={popupPosition.x} y={popupPosition.y}>
              {renderNodePopup(selectedNode, handleClosePopup)}
            </PopupContainer>
          </PopupOverlay>
        )}
      </Container>
    </>
  )
}
